import React, { Component, useState, useEffect, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsReact from 'highcharts-react-official';
import { simpleFetch, refDistrictHistogram, buildOnLoad, getToken } from '../../../utils/helpsers';
import LayoutPage from '../../../templates/Layout/LayoutPage';
import ReportButtons from '../../../components/organisms/BugReport/ReportButtons';
import BoardBuilder from '../../../components/organisms/BoardBuilder/BoardBuilder';
import LollipopChart from '../../../components/organisms/LollipopChart/LollipopChart';
import MapView from '../../../components/organisms/MapView/MapView';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MenuBoxStyles, BoardActionsStyles, BoardGroupCounter } from '../DistrictInsight/DistrictInsight.styles';
import GroupDisplay from '../../../components/molecules/GroupDisplay/GroupDisplay';

class BoardBuilderErrorBoundary extends Component {
  state = { hasError: false, message: '' };

  static getDerivedStateFromError(error) {
    return { hasError: true, message: error.message };
  }

  render() {
    if (this.state.hasError) {
      return (
        <Alert severity="warning" sx={{ m: 2 }}>
          Board builder failed to load — the API may be unreachable.
          Check that the PostgREST backend is running. ({this.state.message})
        </Alert>
      );
    }
    return this.props.children;
  }
}

require('highcharts/modules/exporting')(Highcharts);
require('highcharts/modules/export-data')(Highcharts);
HighchartsMore(Highcharts);

const BIRTHS_COLOR = '#F2A23C';
const KG_COLOR = '#2A6FB5';
const AVG_COLOR = '#773DBD';
// Line-chart series colors: the reference district is yellow (births solid,
// enrollment dotted); the group averages are purple (births solid, enrollment dotted).
const CHART_DISTRICT_COLOR = '#F5B335';
const CHART_GROUP_COLOR = AVG_COLOR;
// The birth-to-enrollment ratio rides the secondary (right) y-axis, so it gets a
// third, distinct color (teal-green) — never confused with the gold district
// count lines or the purple group-average lines on the left axis (#580).
const CHART_RATIO_COLOR = '#1F9E89';

// Grade model: picker value 0 = KG, 1..12 = Grade 1..12. A child born in
// year Y enters KG in school year Y+6 (end-year convention), Grade N in
// Y+6+N.
const GRADE_BASE_OFFSET = 6;
const gradeOffsetOf = (g) => g + GRADE_BASE_OFFSET;
const gradeCode = (g) => (g === 0 ? 'KG' : String(g).padStart(2, '0'));
const gradeLabel = (g) => (g === 0 ? 'KG' : `Grade ${g}`);
const measureLabel = (g) =>
  g === 0
    ? 'Kindergarten Birth Ratio'
    : `${gradeLabel(g)} Birth Ratio`;

const CAPTURE_CAVEAT =
  'Kindergarten enrollment ÷ live births 6 years prior. A ratio above ' +
  '1.0 means the district enrolls more kindergartners than were born in it — a ' +
  'sign of net in-migration (or in-bound open enrollment / private-to-public ' +
  'entry); below 1.0 suggests out-migration or school-choice loss.';

const PROJECTION_CAVEAT =
  'Validated next-year (h=1) projection (#562 Phase B): an enrollment-weighted ' +
  'blend of recent trend and a births-led grade-progression forecast, shown ' +
  'with an 80% range. The strongest single-year signal available (typical ' +
  'error ~2%) — a forecast, not a guarantee. Longer horizons did not pass ' +
  'validation and are not shown.';

const padIrn = (value) => String(value).padStart(6, '0');

const formatRatio = (n) =>
  n === null || n === undefined || Number.isNaN(n) ? '' : n.toFixed(2);

const formatCount = (n) =>
  n === null || n === undefined ? '' : n.toLocaleString();

// Feature flag — the next-year "Projected Enrollment" element is hidden by
// default and enabled per-user via the URL: ?projection=on turns it on (and
// persists in localStorage), ?projection=off hides it again.
const PROJECTION_FLAG_KEY = 'ff_projection';
const projectionFlagEnabled = () => {
  try {
    const q = new URLSearchParams(window.location.search).get('projection');
    if (['on', '1', 'true'].includes(q)) {
      window.localStorage.setItem(PROJECTION_FLAG_KEY, 'true');
    } else if (['off', '0', 'false'].includes(q)) {
      window.localStorage.removeItem(PROJECTION_FLAG_KEY);
    }
    return window.localStorage.getItem(PROJECTION_FLAG_KEY) === 'true';
  } catch (e) {
    return false;
  }
};

const joinSeries = (birthRows, enrollRows, offset) => {
  const births = {};
  const enroll = {};
  const ratio = {};
  const enrollBySy = {};
  const ratioBySy = {};

  birthRows.forEach((r) => {
    births[r.birth_year] = Number(r.live_birth_count);
  });
  enrollRows.forEach((r) => {
    // Suppressed cells arrive as enrollment_count null — treat as missing,
    // never 0 (Number(null) === 0 would fabricate a 0.00 ratio).
    if (r.enrollment_count == null) return;
    const cohort = r.school_year - offset;
    const count = Number(r.enrollment_count);
    enroll[cohort] = count;
    enrollBySy[r.school_year] = count;
  });

  const years = [
    ...new Set([
      ...Object.keys(births).map(Number),
      ...Object.keys(enroll).map(Number),
    ]),
  ].sort((a, b) => a - b);

  years.forEach((y) => {
    if (births[y] != null && enroll[y] != null && births[y] > 0) {
      ratio[y] = enroll[y] / births[y];
      ratioBySy[y + offset] = ratio[y];
    }
  });

  return { years, births, enroll, ratio, enrollBySy, ratioBySy };
};

const buildComparisonSeries = (birthRows, enrollRows, offset) => {
  const byIrn = {};
  birthRows.forEach((r) => {
    const irn = String(r.irn);
    if (!byIrn[irn]) byIrn[irn] = { births: [], enrolls: [] };
    byIrn[irn].births.push(r);
  });
  enrollRows.forEach((r) => {
    const irn = String(r.irn);
    if (!byIrn[irn]) byIrn[irn] = { births: [], enrolls: [] };
    byIrn[irn].enrolls.push(r);
  });

  const result = {};
  Object.entries(byIrn).forEach(([irn, data]) => {
    result[irn] = joinSeries(data.births, data.enrolls, offset);
  });
  return result;
};

const percentile = (sorted, p) => {
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (idx - lo);
};

const computeGroupStats = (compSeries, allYears) => {
  const avg = { births: {}, enroll: {}, ratio: {} };
  const range = { birthRange: [], enrollRange: [] };

  allYears.forEach((y, i) => {
    const bVals = [];
    const eVals = [];
    const rVals = [];

    Object.values(compSeries).forEach((s) => {
      if (s.births[y] != null) bVals.push(s.births[y]);
      if (s.enroll[y] != null) eVals.push(s.enroll[y]);
      if (s.ratio[y] != null) rVals.push(s.ratio[y]);
    });

    if (bVals.length > 0) {
      avg.births[y] = bVals.reduce((a, b) => a + b, 0) / bVals.length;
      bVals.sort((a, b) => a - b);
      range.birthRange.push([i, percentile(bVals, 0.25), percentile(bVals, 0.75)]);
    }
    if (eVals.length > 0) {
      avg.enroll[y] = eVals.reduce((a, b) => a + b, 0) / eVals.length;
      eVals.sort((a, b) => a - b);
      range.enrollRange.push([i, percentile(eVals, 0.25), percentile(eVals, 0.75)]);
    }
    if (rVals.length > 0) {
      avg.ratio[y] = rVals.reduce((a, b) => a + b, 0) / rVals.length;
    }
  });

  return { avg, range };
};

function LiveBirthDashboard() {
  const { user } = useAuth0();
  const metadata = user
    ? user[`${process.env.REACT_APP_AUTH0_KEY}/district`]
    : null;
  const navigate = useNavigate();

  const [builderData, setBuilderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [birthRows, setBirthRows] = useState([]);
  const [gradeRows, setGradeRows] = useState([]); // reference district, ALL grades
  const [projRows, setProjRows] = useState([]); // reference district next-year projection
  const [compLoading, setCompLoading] = useState(false);
  const [gradeLoading, setGradeLoading] = useState(false);
  const [compBirthRows, setCompBirthRows] = useState([]);
  const [compEnrollRows, setCompEnrollRows] = useState([]); // group, selected grade only
  const [districtNames, setDistrictNames] = useState({});
  const [focalYear, setFocalYear] = useState(null);
  const [selectedGrade, setSelectedGrade] = useState(0); // 0 = KG (default)
  const [mapData, setMapData] = useState(null);

  const gradeOffset = gradeOffsetOf(selectedGrade);

  useEffect(() => {
    if (!metadata) {
      navigate('/AccessDenied', { replace: true });
      return;
    }
    const sessionType = metadata.sessionType || 'forbidden';
    if (sessionType === 'district-access') {
      window.localStorage.setItem('session-type', 'district-access');
    } else if (
      sessionType === 'full-access' ||
      sessionType === 'multiple-district-access'
    ) {
      window.localStorage.setItem('session-type', sessionType);
    } else {
      navigate('/AccessDenied', { replace: true });
    }
  }, [metadata, navigate]);

  useEffect(() => {
    if (!user) return;
    buildOnLoad({ user, callback: setBuilderData, token: getToken() });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    simpleFetch(
      `${process.env.REACT_APP_API_URL}/hvd_district?order=label`,
    ).then((data) => {
      if (data) {
        const lookup = {};
        data.forEach((d) => {
          lookup[padIrn(d.value)] = d.label;
        });
        setDistrictNames(lookup);
      }
    });
  }, [user]);

  useEffect(() => {
    if (!builderData?.referenceDistrict?.value) return;
    const irn = padIrn(builderData.referenceDistrict.value);
    setLoading(true);
    setBirthRows([]);
    setGradeRows([]);
    setProjRows([]);

    Promise.all([
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/live_birth_district_year?irn=eq.${irn}&order=birth_year`,
      ),
      // All grades at once (~14 grades x 10 years) so grade switches are
      // instant for the reference district and the picker can show real
      // per-grade cohort coverage.
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/grade_enrollment_district_year?irn=eq.${irn}&order=grade_order,school_year`,
      ),
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/enrollment_projection_district_year?irn=eq.${irn}`,
      ),
    ])
      .then(([b, g, pj]) => {
        setBirthRows(b || []);
        setGradeRows(g || []);
        setProjRows(pj || []);
      })
      .finally(() => setLoading(false));
  }, [builderData?.referenceDistrict?.value]);

  // Group data that does not depend on the selected grade.
  useEffect(() => {
    if (!builderData?.irns) {
      setCompBirthRows([]);
      setMapData(null);
      return;
    }
    setCompLoading(true);

    const refIrn = padIrn(builderData.referenceDistrict.value);
    // BoardBuilder returns unpadded IRNs; live_birth MVs store zero-padded 6-char strings
    const paddedIrns = String(builderData.irns)
      .split(',')
      .map((s) => padIrn(s.trim()))
      .join(',');

    Promise.all([
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/live_birth_district_year?irn=in.(${paddedIrns})&order=irn,birth_year`,
      ),
      simpleFetch(
        `${process.env.REACT_APP_API_URL}/hvd_polygons_mcd?irn=in.(${paddedIrns},${refIrn})`,
      ),
    ])
      .then(([b, polygons]) => {
        setCompBirthRows(b || []);
        setMapData(polygons || null);
      })
      .finally(() => setCompLoading(false));
  }, [builderData?.irns, builderData?.referenceDistrict?.value]);

  // Group enrollment for the selected grade only — same row volume as the
  // old KG-only query, re-fetched when the grade changes.
  useEffect(() => {
    if (!builderData?.irns) {
      setCompEnrollRows([]);
      return;
    }
    setGradeLoading(true);
    setCompEnrollRows([]);

    const paddedIrns = String(builderData.irns)
      .split(',')
      .map((s) => padIrn(s.trim()))
      .join(',');

    simpleFetch(
      `${process.env.REACT_APP_API_URL}/grade_enrollment_district_year?irn=in.(${paddedIrns})&grade=eq.${gradeCode(selectedGrade)}&order=irn,school_year`,
    )
      .then((rows) => setCompEnrollRows(rows || []))
      .finally(() => setGradeLoading(false));
  }, [builderData?.irns, selectedGrade]);

  const refEnrollRows = useMemo(
    () => gradeRows.filter((r) => r.grade === gradeCode(selectedGrade)),
    [gradeRows, selectedGrade],
  );

  const refSeries = useMemo(
    () => joinSeries(birthRows, refEnrollRows, gradeOffset),
    [birthRows, refEnrollRows, gradeOffset],
  );

  // Derived from raw rows (not stored as state) so a grade switch can never
  // briefly join an old grade's enrollment with the new offset.
  const comparisonSeries = useMemo(
    () => buildComparisonSeries(compBirthRows, compEnrollRows, gradeOffset),
    [compBirthRows, compEnrollRows, gradeOffset],
  );

  const birthYearSet = useMemo(
    () => new Set(birthRows.map((r) => Number(r.birth_year))),
    [birthRows],
  );

  // Picker options carry real cohort coverage so thin grades are obvious
  // (enrollment starts SY 2015-16 — Grade 12 overlaps only ~2 birth cohorts).
  const gradeOptions = useMemo(
    () =>
      [...Array(13).keys()].map((g) => {
        const offset = gradeOffsetOf(g);
        const cohorts = gradeRows.filter(
          (r) =>
            r.grade === gradeCode(g) &&
            r.enrollment_count != null &&
            birthYearSet.has(r.school_year - offset),
        ).length;
        return {
          value: g,
          label: `${g === 0 ? 'Kindergarten (KG)' : `Grade ${g}`} — ${cohorts} cohort${cohorts === 1 ? '' : 's'}`,
        };
      }),
    [gradeRows, birthYearSet],
  );

  const coveredCohorts = useMemo(
    () => Object.keys(refSeries.ratio).length,
    [refSeries.ratio],
  );

  // Validated next-year (h=1) projection — one row per district.
  const projection = useMemo(() => {
    if (!projRows.length) return null;
    const r = projRows[0];
    const origin = Number(r.enrollment_origin);
    return {
      ...r,
      changePct: Number(r.projected_change_pct),
      lo: Math.round(origin * (1 + Number(r.lo80_pct) / 100)),
      hi: Math.round(origin * (1 + Number(r.hi80_pct) / 100)),
    };
  }, [projRows]);

  // Off by default; enabled per-user via ?projection=on (see projectionFlagEnabled).
  const projectionEnabled = useMemo(() => projectionFlagEnabled(), []);

  const allYears = useMemo(() => {
    const yearSet = new Set(refSeries.years);
    Object.values(comparisonSeries).forEach((s) => {
      s.years.forEach((y) => yearSet.add(y));
    });
    return [...yearSet].sort((a, b) => a - b);
  }, [refSeries, comparisonSeries]);

  const hasComparison = Object.keys(comparisonSeries).length > 0;
  const refIrn = builderData?.referenceDistrict?.value
    ? padIrn(builderData.referenceDistrict.value)
    : null;

  const { avg: groupAvg } = useMemo(
    () =>
      hasComparison
        ? computeGroupStats(comparisonSeries, allYears)
        : { avg: null },
    [comparisonSeries, allYears, hasComparison],
  );

  useEffect(() => {
    const ratioYears = allYears.filter((y) => refSeries.ratio[y] != null);
    if (ratioYears.length > 0) {
      setFocalYear(ratioYears[ratioYears.length - 1]);
    }
  }, [allYears, refSeries.ratio]);

  const cardStats = useMemo(() => {
    if (refSeries.years.length === 0) return null;

    const birthYears = refSeries.years.filter((y) => refSeries.births[y] != null);
    const ratioYears = refSeries.years.filter((y) => refSeries.ratio[y] != null);
    const latestBirthYear = birthYears[birthYears.length - 1];
    const latestRatioYear = ratioYears[ratioYears.length - 1];
    const latestBirths = refSeries.births[latestBirthYear];
    const latestEnroll = refSeries.enroll[latestRatioYear];
    const latestRatio = refSeries.ratio[latestRatioYear];
    const cohortBirths = latestRatioYear != null ? refSeries.births[latestRatioYear] : null;
    const totalBirths = birthYears.reduce((sum, y) => sum + (refSeries.births[y] || 0), 0);
    const peakBirths = Math.max(...birthYears.map((y) => refSeries.births[y] || 0));
    const peakYear = birthYears.find((y) => refSeries.births[y] === peakBirths);

    let rankInGroup = null;
    let groupSize = null;
    if (hasComparison && latestRatioYear != null) {
      const allRatios = [];
      const allDistricts = { ...comparisonSeries };
      if (refIrn) allDistricts[refIrn] = refSeries;
      Object.entries(allDistricts).forEach(([irn, s]) => {
        if (s.ratio[latestRatioYear] != null) {
          allRatios.push({ irn, ratio: s.ratio[latestRatioYear] });
        }
      });
      allRatios.sort((a, b) => b.ratio - a.ratio);
      const refIdx = allRatios.findIndex((r) => r.irn === refIrn);
      if (refIdx >= 0) {
        rankInGroup = refIdx + 1;
        groupSize = allRatios.length;
      }
    }

    return {
      latestBirthYear, latestBirths,
      latestRatioYear, latestEnroll, latestRatio, cohortBirths,
      totalBirths,
      peakBirths, peakYear,
      birthYearRange: `${birthYears[0]}–${birthYears[birthYears.length - 1]}`,
      enrollYearRange: ratioYears.length > 0 ? `${ratioYears[0]}–${ratioYears[ratioYears.length - 1]}` : null,
      rankInGroup, groupSize,
    };
  }, [refSeries, hasComparison, comparisonSeries, refIrn]);

  const chartOptions = useMemo(() => {
    const cats = allYears.map(String);
    const refBirthsData = allYears.map((y) => refSeries.births[y] ?? null);
    const refEnrollData = allYears.map((y) => refSeries.enroll[y] ?? null);
    // District-name suffix used by every reference-district series (counts and
    // ratios alike), e.g. " — Ada Ex Vill SD, Hardin (045187)".
    const refSuffix = builderData?.referenceDistrict
      ? ` — ${builderData.referenceDistrict.label}`
      : '';

    const chartSeries = [
      {
        name: `Num Births${builderData?.referenceDistrict ? ' — ' + builderData.referenceDistrict.label : ''}`,
        data: refBirthsData,
        color: CHART_DISTRICT_COLOR,
        yAxis: 0,
        lineWidth: 3,
        marker: { radius: 4 },
        zIndex: 5,
      },
      {
        name: `${gradeLabel(selectedGrade)} Enrollment${builderData?.referenceDistrict ? " — " + builderData.referenceDistrict.label : ""}`,
        data: refEnrollData,
        color: CHART_DISTRICT_COLOR,
        dashStyle: 'Dot',
        yAxis: 0,
        lineWidth: 3,
        marker: { radius: 4 },
        zIndex: 5,
      },
    ];

    if (groupAvg) {
      chartSeries.push({
        name: 'Group Avg Births',
        data: allYears.map((y) =>
          groupAvg.births[y] != null ? Math.round(groupAvg.births[y]) : null,
        ),
        color: CHART_GROUP_COLOR,
        lineWidth: 2,
        marker: { enabled: false },
        yAxis: 0,
        zIndex: 3,
      });
      chartSeries.push({
        name: `Group Avg ${gradeLabel(selectedGrade)}`,
        data: allYears.map((y) =>
          groupAvg.enroll[y] != null ? Math.round(groupAvg.enroll[y]) : null,
        ),
        color: CHART_GROUP_COLOR,
        dashStyle: 'Dot',
        lineWidth: 2,
        marker: { enabled: false },
        yAxis: 0,
        zIndex: 3,
      });
    }

    // #580 — birth-to-enrollment ratio on the secondary (right) axis. Keyed by
    // birth year (refSeries.ratio), so each point lines up with the count series
    // above; rebuilt on the grade picker via gradeOffset. Distinct teal color +
    // dash so it never reads as a count line on the left axis.
    chartSeries.push({
      name: `${measureLabel(selectedGrade)}${refSuffix}`,
      data: allYears.map((y) => refSeries.ratio[y] ?? null),
      color: CHART_RATIO_COLOR,
      dashStyle: 'ShortDash',
      yAxis: 1,
      lineWidth: 3,
      marker: { radius: 4 },
      zIndex: 4,
      tooltip: { valueDecimals: 2 },
    });

    if (groupAvg) {
      // Faint companion: the comparison group's average birth ratio on the same axis.
      chartSeries.push({
        name: `Group Avg ${measureLabel(selectedGrade)}`,
        data: allYears.map((y) =>
          groupAvg.ratio[y] != null ? groupAvg.ratio[y] : null,
        ),
        color: CHART_RATIO_COLOR,
        dashStyle: 'Dot',
        opacity: 0.5,
        yAxis: 1,
        lineWidth: 2,
        marker: { enabled: false },
        zIndex: 2,
        tooltip: { valueDecimals: 2 },
      });
    }

    return {
      chart: { type: 'line', height: 480, backgroundColor: '#fff' },
      title: { text: `${selectedGrade === 0 ? "Kindergarten" : gradeLabel(selectedGrade)} Enrollment and Live Births` },
      xAxis: {
        categories: cats,
        title: { text: `Birth Year / ${gradeLabel(selectedGrade)} School Year` },
        labels: {
          formatter() {
            const birthYear = Number(this.value);
            return `${birthYear}<br/><span style="color: ${KG_COLOR};">${birthYear + gradeOffset}</span>`;
          },
        },
      },
      yAxis: [
        {
          title: { text: 'Number of Births' },
          min: 0,
          allowDecimals: false,
        },
        {
          // This axis hosts the birth-ratio line(s), centered ~1.0; the legend
          // names each line (#580).
          title: { text: 'Ratio (×)' },
          min: 0,
          // softMax (not a hard max) keeps the axis steady at 1.5 for typical
          // ratios but still extends for districts above 1.5 (net in-migration),
          // so a high ratio line is never silently clipped (#580).
          softMax: 1.5,
          opposite: true,
          gridLineWidth: 0,
        },
      ],
      tooltip: { shared: true },
      plotOptions: {
        line: {
          marker: { enabled: true, radius: 3 },
          connectNulls: false,
        },
      },
      legend: { enabled: true },
      credits: { enabled: false },
      exporting: {
        buttons: {
          contextButton: {
            menuItems: [
              'viewFullscreen',
              'printChart',
              'separator',
              'downloadPNG',
              'downloadPDF',
              'downloadCSV',
              'downloadXLS',
            ],
          },
        },
      },
      series: chartSeries,
    };
  }, [allYears, refSeries, groupAvg, builderData?.referenceDistrict, gradeOffset, selectedGrade]);

  const comparisonTableData = useMemo(() => {
    if (!hasComparison || focalYear == null) return [];

    const rows = [];

    if (refIrn) {
      rows.push({
        irn: refIrn,
        name:
          builderData?.referenceDistrict?.label ||
          districtNames[refIrn] ||
          refIrn,
        births: refSeries.births[focalYear] ?? null,
        enroll: refSeries.enroll[focalYear] ?? null,
        ratio: refSeries.ratio[focalYear] ?? null,
        isRef: true,
      });
    }

    Object.entries(comparisonSeries).forEach(([irn, s]) => {
      if (irn === refIrn) return;
      rows.push({
        irn,
        name: districtNames[irn] || irn,
        births: s.births[focalYear] ?? null,
        enroll: s.enroll[focalYear] ?? null,
        ratio: s.ratio[focalYear] ?? null,
        isRef: false,
      });
    });

    const ranked = rows
      .filter((r) => r.ratio != null)
      .sort((a, b) => b.ratio - a.ratio);
    ranked.forEach((r, i) => {
      r.rank = i + 1;
    });
    const totalRanked = ranked.length;
    rows.forEach((r) => {
      r.totalRanked = totalRanked;
    });

    rows.sort((a, b) => {
      if (a.isRef && !b.isRef) return -1;
      if (!a.isRef && b.isRef) return 1;
      if (a.ratio == null && b.ratio == null) return 0;
      if (a.ratio == null) return 1;
      if (b.ratio == null) return -1;
      return b.ratio - a.ratio;
    });

    return rows;
  }, [
    hasComparison,
    focalYear,
    refIrn,
    refSeries,
    comparisonSeries,
    builderData,
    districtNames,
  ]);

  const compTableAvg = useMemo(() => {
    if (!groupAvg || focalYear == null) return null;
    return {
      births: groupAvg.births[focalYear] ?? null,
      enroll: groupAvg.enroll[focalYear] ?? null,
      ratio: groupAvg.ratio[focalYear] ?? null,
    };
  }, [groupAvg, focalYear]);

  const focalYearOptions = useMemo(() => {
    const ratioYears = allYears.filter((y) => refSeries.ratio[y] != null);
    return ratioYears.map((y) => ({ value: y, label: String(y) }));
  }, [allYears, refSeries.ratio]);

  const lollipopData = useMemo(() => {
    if (!hasComparison || focalYear == null) return null;

    const items = [];
    const allDistricts = { ...comparisonSeries };
    if (refIrn) {
      allDistricts[refIrn] = refSeries;
    }

    Object.entries(allDistricts).forEach(([irn, s]) => {
      if (s.ratio[focalYear] != null) {
        items.push({
          irn,
          name: irn === refIrn
            ? (builderData?.referenceDistrict?.label || districtNames[irn] || irn)
            : (districtNames[irn] || irn),
          ratio: s.ratio[focalYear],
          isRef: irn === refIrn,
        });
      }
    });

    items.sort((a, b) => b.ratio - a.ratio);

    const categories = items.map((d) => d.name);
    const data = items.map((d) =>
      d.isRef ? refDistrictHistogram(+d.ratio.toFixed(3), items) : +d.ratio.toFixed(3),
    );

    const ratios = items.map((d) => d.ratio);
    const ranges =
      ratios.length > 0
        ? [Math.min(...ratios), Math.max(...ratios)]
        : null;

    return { categories, data, ranges };
  }, [
    hasComparison,
    focalYear,
    comparisonSeries,
    refSeries,
    refIrn,
    builderData,
    districtNames,
  ]);

  const mapDataWithRatio = useMemo(() => {
    if (!mapData || !Array.isArray(mapData) || focalYear == null) return null;

    const allDistricts = { ...comparisonSeries };
    if (refIrn) {
      allDistricts[refIrn] = refSeries;
    }

    return mapData.map((polygon) => {
      const irn = padIrn(polygon.irn);
      const series = allDistricts[irn];
      return {
        ...polygon,
        persistence_ratio: series?.ratio?.[focalYear] ?? null,
        lb_births: series?.births?.[focalYear] ?? null,
        lb_enroll: series?.enroll?.[focalYear] ?? null,
        lb_focal_year: focalYear,
        lb_grade_label: gradeLabel(selectedGrade),
        lb_offset: gradeOffset,
      };
    });
  }, [mapData, focalYear, comparisonSeries, refSeries, refIrn, selectedGrade, gradeOffset]);

  const [btnFilter, setBtnFilter] = useState(null);
  const openFilters = Boolean(btnFilter);

  const clickFilters = (event) => { setBtnFilter(event.currentTarget); };
  const closeFilters = () => { setBtnFilter(null); };
  const handleFilter = () => { setBtnFilter(null); };

  const topContent = (
    <Box className="board-actions" sx={BoardActionsStyles}>
      <Grid container className="board-actions__wrapper" spacing={2}>
        <Grid item xs={12} lg={3} className="board-actions__board-menu" display="flex">
          <Tooltip title="Filters">
            <Button
              id="button-filters"
              aria-controls="menu-filters"
              aria-haspopup="true"
              aria-expanded={openFilters ? 'true' : 'false'}
              variant="contained"
              onClick={clickFilters}
              color="grape"
              className="board-menu__filters"
            >
              <FilterAltOutlinedIcon />
              Filters
            </Button>
          </Tooltip>
          <Menu
            id="menuFilters"
            MenuListProps={{ 'aria-labelledby': 'button-filters' }}
            anchorEl={btnFilter}
            open={openFilters}
            onClose={closeFilters}
            sx={MenuBoxStyles}
          >
            <MenuItem disableRipple>
              <BoardBuilderErrorBoundary>
                <BoardBuilder
                  groupEnabled={true}
                  yearEnabled={false}
                  boardData={setBuilderData}
                  onSubmit={() => handleFilter()}
                />
              </BoardBuilderErrorBoundary>
            </MenuItem>
          </Menu>
        </Grid>
        {builderData?.referenceDistrict && builderData?.groupCount && (
          <Grid item xs={12} lg={9} className="group-counter" sx={BoardGroupCounter}>
            <div className="group-counter__wrapper">
              <div className="group-counter__versus">
                {`${builderData.referenceDistrict.label} - vs - `}
                <GroupDisplay groupData={{ groupInfo: builderData.groupInfo, groupType: builderData.groupType }} />
              </div>
              <div className="group-counter__result">
                {`${builderData.groupCount} districts in the comparison group.`}
              </div>
            </div>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  return (
    <LayoutPage
      pageTitle={<div>Enrollment Live Birth Index <span style={{ color: '#FFDF00' }}>(Beta)</span></div>}
      documentTitle="Enrollment Live Birth Index (Beta)"
      description="Live births by district by year, paired with grade-level enrollment in later school years."
      type="dashboard"
      customClass="live-birth"
      topContent={topContent}
    >
      <Container maxWidth="xl" fixed>
        {/* Row 1: Live Birth summary card + Map */}
        <Grid container spacing={2} columns={12}>
          {cardStats && builderData?.referenceDistrict && (
            <Grid item xs={12} lg={6} className="section section--district-data">
              <Box sx={{ bgcolor: 'primary.main', height: '100%', p: '7px' }}>
                <Box sx={{ p: '6px' }}>
                  <Box sx={{ color: '#fff', mb: '6px' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, px: '10px', pb: '4px', m: 0 }}>
                      {builderData.referenceDistrict.label.split(',')[0]}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, px: '10px' }}>
                      <Typography variant="body2" sx={{ color: 'primary.light', fontWeight: 700 }}>IRN:</Typography>
                      <Typography variant="body2" sx={{ color: '#fff' }}>{refIrn}</Typography>
                      <Typography variant="body2" sx={{ color: 'primary.light', fontWeight: 700, ml: 1 }}>County:</Typography>
                      <Typography variant="body2" sx={{ color: '#fff' }}>{builderData.referenceDistrict.label.split(',')[1]?.trim()}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ bgcolor: '#fff', p: '17px', minHeight: { sm: '100px', md: '280px' } }}>
                    {/* Typography matched to the District Insights card: all cells at
                        the standard (~1rem) size, labels bold primary.dark, values
                        plain (normal weight, default dark color). */}
                    <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: 'initial' } }}>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>
                            Latest Births ({cardStats.latestBirthYear})
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>
                            {formatCount(cardStats.latestBirths)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>
                            Latest {gradeLabel(selectedGrade)} Enrollment ({cardStats.latestRatioYear ? cardStats.latestRatioYear + gradeOffset : "—"})
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>
                            {formatCount(cardStats.latestEnroll)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>
                            Births {gradeOffset} Years Prior ({cardStats.latestRatioYear ?? "—"})
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>
                            {formatCount(cardStats.cohortBirths)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>
                            {measureLabel(selectedGrade)} ({cardStats.latestRatioYear ? cardStats.latestRatioYear + gradeOffset : "—"})
                            {selectedGrade === 0 && (
                              <Tooltip title={CAPTURE_CAVEAT}>
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', ml: 0.5, verticalAlign: 'text-bottom' }} />
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>
                            {formatRatio(cardStats.latestRatio)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>Peak Births</TableCell>
                          <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>{formatCount(cardStats.peakBirths)} ({cardStats.peakYear})</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>Total Births ({cardStats.birthYearRange})</TableCell>
                          <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>{formatCount(cardStats.totalBirths)}</TableCell>
                        </TableRow>
                        {projectionEnabled && projection && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: '1px dotted #ccc', pl: 0 }}>
                              Projected Enrollment ({projection.projected_year})
                              <Tooltip title={PROJECTION_CAVEAT}>
                                <InfoOutlinedIcon sx={{ fontSize: '1rem', ml: 0.5, verticalAlign: 'text-bottom' }} />
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ borderBottom: '1px dotted #ccc' }}>
                              <Box>
                                <Typography
                                  component="span"
                                  sx={{ fontWeight: 400 }}
                                  aria-label={`Projected enrollment ${projection.projected_year}: ${formatCount(projection.projected_total)}, ${projection.changePct > 0 ? 'up' : 'down'} ${Math.abs(projection.changePct)} percent`}
                                >
                                  {formatCount(projection.projected_total)}
                                  <Typography component="span" sx={{ ml: 0.5, fontSize: '0.9rem', color: projection.changePct < 0 ? 'error.main' : projection.changePct > 0 ? 'success.main' : 'text.secondary' }}>
                                    {projection.changePct > 0 ? '+' : ''}{projection.changePct}%
                                  </Typography>
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                  80% range: {formatCount(projection.lo)}–{formatCount(projection.hi)}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                        {cardStats.rankInGroup && (
                          <TableRow>
                            <TableCell sx={{ fontWeight: 700, color: 'primary.dark', borderBottom: 'none', pl: 0 }}>Rank in Group ({gradeLabel(selectedGrade)} Birth Ratio)</TableCell>
                            <TableCell sx={{ borderBottom: 'none' }}>
                              {cardStats.rankInGroup} / {cardStats.groupSize}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
          {mapDataWithRatio && refIrn && (
            <Grid item xs={12} lg={6} className="section section--district-map">
              <MapView
                data={mapDataWithRatio}
                districtIrn={Number(refIrn)}
                choroplethVar="persistence_ratio"
                type="live_birth"
              />
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 3, mb: 1 }}>
          <p style={{ margin: 0, color: '#666' }}>
            Source: Ohio Department of Health (births), Ohio Department of
            Education and Workforce (grade-level enrollment).
          </p>
        </Box>

        {/* Grade picker — governs every measure on the dashboard */}
        {builderData?.referenceDistrict && gradeRows.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 1, mb: 1 }}>
            <Autocomplete
              id="grade-picker"
              size="small"
              disableClearable
              options={gradeOptions}
              isOptionEqualToValue={(option, value) =>
                option.value === value.value
              }
              getOptionLabel={(option) => option.label || ''}
              value={
                gradeOptions.find((o) => o.value === selectedGrade) || null
              }
              onChange={(e, newVal) => {
                if (newVal) setSelectedGrade(newVal.value);
              }}
              sx={{ width: 280 }}
              renderInput={(params) => (
                <TextField {...params} label="Compare births to" />
              )}
            />
            <Typography variant="body2" color="text.secondary">
              {`${coveredCohorts} birth cohort${coveredCohorts === 1 ? '' : 's'} covered for ${gradeLabel(selectedGrade)}.`}
            </Typography>
            {coveredCohorts > 0 && coveredCohorts < 4 && (
              <Typography variant="body2" sx={{ color: 'warning.main' }}>
                Limited coverage: enrollment data begins SY 2015-16, so few
                birth cohorts overlap for {gradeLabel(selectedGrade)}.
              </Typography>
            )}
          </Box>
        )}

        <Grid container spacing={3} direction="column">
          {/* Cross-district ratio comparison (lollipop) — reordered to top */}
          {hasComparison && lollipopData && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <LollipopChart
                  title={`${measureLabel(selectedGrade)} (${focalYear ? focalYear + gradeOffset : ""})`}
                  description={`${gradeLabel(selectedGrade)} enrollment as a ratio of births ${gradeOffset} years prior, for each district in the comparison group. Reference district highlighted in gold.`}
                  data={lollipopData}
                  id="lb-ratio-lollipop"
                  ranges={lollipopData.ranges}
                />
              </Paper>
            </Grid>
          )}

          {/* Time-series chart — Enrollment + Live Birth */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              {refSeries.years.length > 0 ? (
                <HighchartsReact
                  highcharts={Highcharts}
                  options={chartOptions}
                />
              ) : (
                <Box sx={{ p: 4, textAlign: 'center', color: '#888' }}>
                  Chart will appear after a district is selected.
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Reference district crosstab */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, overflowX: 'auto' }}>
              {(loading || compLoading || gradeLoading) && <p>Loading…</p>}
              {!loading &&
                refSeries.years.length === 0 &&
                builderData?.referenceDistrict && (
                  <p>
                    No live-birth or enrollment data available for
                    this district.
                  </p>
                )}
              {!loading && refSeries.years.length > 0 && (
                <TableContainer>
                  <Table
                    size="small"
                    aria-label="Enrollment Live Birth Index crosstab"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 240 }}>
                          Measure
                        </TableCell>
                        {refSeries.years.map((y) => (
                          <TableCell
                            key={y}
                            align="right"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {y}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell
                          sx={{ color: BIRTHS_COLOR, fontWeight: 600 }}
                        >
                          Num Births
                        </TableCell>
                        {refSeries.years.map((y) => (
                          <TableCell key={y} align="right">
                            {formatCount(refSeries.births[y])}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: KG_COLOR, fontWeight: 600 }}>
                          {`${gradeLabel(selectedGrade)} Enrollment`}
                        </TableCell>
                        {refSeries.years.map((y) => (
                          <TableCell key={y} align="right">
                            {formatCount(refSeries.enrollBySy[y])}
                          </TableCell>
                        ))}
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {measureLabel(selectedGrade)}
                        </TableCell>
                        {refSeries.years.map((y) => (
                          <TableCell key={y} align="right">
                            {formatRatio(refSeries.ratioBySy[y])}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* Cross-District Comparison section */}
          {hasComparison && focalYearOptions.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">
                  Cross-District Comparison
                </Typography>
                <Autocomplete
                  id="focal-year"
                  size="small"
                  options={focalYearOptions}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  getOptionLabel={(option) => option.label || ''}
                  value={
                    focalYear != null
                      ? { value: focalYear, label: String(focalYear) }
                      : null
                  }
                  onChange={(e, newVal) => {
                    if (newVal) setFocalYear(newVal.value);
                  }}
                  sx={{ width: 140 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Focal Year" />
                  )}
                />
                {builderData?.groupCount && (
                  <Typography variant="body2" color="text.secondary">
                    {builderData.groupCount} districts in group
                  </Typography>
                )}
              </Box>
            </Grid>
          )}

          {/* Cross-district comparison table */}
          {hasComparison && comparisonTableData.length > 0 && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2 }}>
                <TableContainer>
                  <Table size="small" aria-label="Cross-district comparison">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', minWidth: 280 }}>
                          District
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Births
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          {`${gradeLabel(selectedGrade)} Enrollment`}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Birth Ratio
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold" }}>
                          Rank
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {comparisonTableData.map((row) => (
                        <TableRow
                          key={row.irn}
                          sx={
                            row.isRef
                              ? { backgroundColor: '#FFF8E1' }
                              : {}
                          }
                        >
                          <TableCell
                            sx={row.isRef ? { fontWeight: 700 } : {}}
                          >
                            {row.name}
                            {row.isRef ? ' (ref)' : ''}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={row.isRef ? { fontWeight: 700 } : {}}
                          >
                            {formatCount(row.births)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={row.isRef ? { fontWeight: 700 } : {}}
                          >
                            {formatCount(row.enroll)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={row.isRef ? { fontWeight: 700 } : {}}
                          >
                            {formatRatio(row.ratio)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={row.isRef ? { fontWeight: 700 } : {}}
                          >
                            {row.rank
                              ? `${row.rank}/${row.totalRanked}`
                              : '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                      {compTableAvg && (
                        <TableRow
                          sx={{
                            backgroundColor: '#F3F0FF',
                            borderTop: '2px solid #773DBD',
                          }}
                        >
                          <TableCell
                            sx={{ fontWeight: 700, color: AVG_COLOR }}
                          >
                            Group Average
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCount(
                              compTableAvg.births != null
                                ? Math.round(compTableAvg.births)
                                : null,
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatCount(
                              compTableAvg.enroll != null
                                ? Math.round(compTableAvg.enroll)
                                : null,
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {formatRatio(compTableAvg.ratio)}
                          </TableCell>
                          <TableCell align="right">—</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          )}
        </Grid>

        <ReportButtons
          boardType="Enrollment Live Birth Index"
          collectedData={{ districtInfo: builderData?.referenceDistrict }}
        />
      </Container>
    </LayoutPage>
  );
}

export default LiveBirthDashboard;
